import React, { useState, useContext, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, FormControl, FormControlLabel, FormHelperText, MenuItem } from '@mui/material';
import { StoreContext } from '../../components/Store';
import PropTypes from 'prop-types';
import { getTraderDetails, postTraderDetails, putTraderDetails, deleteTrader } from '../../api/settings';
import LoadingSpinner from '../../components/LoadingSpinner';
import { fetchCountryData, getEORIValidation } from '../../api/util';
import CustomCheckbox from '../../components/CustomCheckbox';
import { MANDATORY_EORI_COUNTRIES } from '../Decs/utils';
const paginationModel = { page: 0, pageSize: 50 };

export default function DataTable(props) {
  const [rowData, setRowData] = useState([]);
  const [open, setOpen] = useState(false);
  const [traderData, setTraderData] = useState({});
  const [addTrader, setAddTrader] = useState(false);
  const [countriesData, setCountriesData] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const store = useContext(StoreContext);
  const companyId = props.companyId || store.eoriCompany[0];
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isReadOnly } = props;

  const handleOnChange = (event) => {
    if (event?.$isDayjsObject) {
      setTraderData((prevData) => ({
        ...prevData,
        ['arrivalDate']: new Date(event),
      }));
    } else if (event.target.type === 'checkbox') {
      setTraderData((prevData) => ({
        ...prevData,
        [event.target.name]: event.target.checked,
      }));
    } else {
      let title = event.target.name;
      let value = event.target.value;
      setTraderData((prevData) => ({
        ...prevData,
        [title]: value,
      }));
    }
  };

  // handle trimming of data when fields lose focus
  const handleOnBlur = (e) => {
    const { name, value } = e.target;
    if (typeof value === 'string') {
      setTraderData((prevData) => ({
        ...prevData,
        [name]: value.trim(),
      }));
    }
  };

  const apiCall = async () => {
    setIsLoading(true);

    const countryList = await fetchCountryData();
    let traderDetails = await getTraderDetails(companyId || sessionStorage.getItem('companyid'));

    store.traderDetails[1](traderDetails);
    setCountriesData(countryList);

    for (const element of traderDetails) {
      element['actions'] = 'Edit';
    }

    setRowData(traderDetails);
    setIsLoading(false);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        await apiCall();
      } catch (err) {
        console.log(err);
      }
    }
    fetchData();
  }, []);

  const handleClose = () => setOpen(false);
  const handleClick = (dataClickedItem) => {
    if (Object.keys(dataClickedItem).length > 0 && dataClickedItem.constructor === Object) {
      let itemsRow = dataClickedItem.row;
      for (const rowItem in itemsRow) {
        setTraderData((prevData) => ({
          ...prevData,
          [rowItem]: itemsRow[rowItem],
        }));
      }
    }
  };
  const handleModalClick = (item) => {
    setAddTrader(false);
    setFormErrors({});
    setOpen(true);
  };
  const columns = [
    {
      field: 'eori',
      headerName: 'EORI',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
    },
    {
      field: 'companyName',
      headerName: 'Name',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
    },
    {
      field: 'postCode',
      headerName: 'Postcode',
      type: 'number',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
    },
    {
      field: 'deleted',
      headerName: 'Status',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => (params.value === true ? 'Deleted' : 'Active'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) =>
        !isReadOnly && (
          <span
            onClick={handleModalClick}
            style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {params.value}
          </span>
        ),
    },
  ];

  const countryRequiresEORI = (countryCode) => {
    const selectedCountry = countriesData.find((country) => country.countryCode === countryCode);
    const isEUCountry = selectedCountry?.isEU && MANDATORY_EORI_COUNTRIES.includes(countryCode);
    const isGBCountry = countryCode === 'GB';
    return isEUCountry || isGBCountry;
  };

  const validateTraderForm = async (data) => {
    // checkboxes: traderData.isConsignor || traderData.isConsignee || traderData.isCarrier
    data['checkboxes'] = data.isConsignor || data.isConsignee || data.isCarrier;
    const errors = {};

    const fieldLabels = {
      CompanyName: 'Company Name',
      EORI: 'eori',
      StreetAddress: 'Street Address',
      streetNumber: 'Street Number',
      PostalCode: 'Postcode',
      City: 'Town / City',
      CountryCode: 'Country',
      checkboxes: 'At least one checkbox',
    };

    const isEORIRequired = countryRequiresEORI(data.CountryCode);

    const fieldValidation = {
      CompanyName: ['required'],
      EORI: isEORIRequired ? ['required', 'validEORI'] : ['validEORI'], // Conditional requirement
      StreetAddress: ['required'],
      streetNumber: ['required'],
      PostalCode: ['required'],
      City: ['required'],
      CountryCode: ['required'],
      checkboxes: ['required'],
    };

    for (const field in data) {
      const validations = fieldValidation[field] || [];

      for (const i in validations) {
        const validation = validations[i];
        if (validation === 'required' && (data[field] === '' || !data[field])) {
          // For EORI field, use consistent error message and field name
          if (field === 'EORI') {
            errors['eori'] = 'EORI must be a valid EORI number';
          } else {
            errors[field] = `${fieldLabels[field]} is required.`;
          }
        } else if (validation === 'validEORI') {
          // Only validate EORI format if it's not empty
          if (data[field] && data[field].trim() !== '') {
            const isGB = !!data?.[field]?.startsWith('GB');
            try {
              const isValidEORI = await getEORIValidation(data[field]?.toUpperCase(), isGB);

              // Check if API returned an error or invalid status
              if (isValidEORI.error || isValidEORI.statusDescr === 'In Valid' || !isValidEORI.statusDescr) {
                errors['eori'] = 'EORI must be a valid EORI number';
              }
            } catch (error) {
              // If API fails, treat as invalid EORI
              errors['eori'] = 'EORI must be a valid EORI number';
            }
          }
        }
      }
    }

    return errors;
  };

  const handleEdit = async () => {
    setFormErrors({});
    setIsSubmitting(true);

    const payload = {
      Id: traderData.id,
      RefType: traderData.refType,
      CompanyName: traderData.companyName,
      EORI: traderData.eori,
      streetNumber: traderData.streetNumber,
      StreetAddress: traderData.streetAddress,
      PostalCode: traderData.postCode,
      City: traderData.townCity,
      CountryCode: traderData.Country,
      isConsignor: traderData.isConsignor || false,
      isConsignee: traderData.isConsignee || false,
      isCarrier: traderData.isCarrier || false,
      // checkboxes: traderData.isConsignor || traderData.isConsignee || traderData.isCarrier,
    };

    const errors = await validateTraderForm(payload);

    try {
      if (Object.keys(errors).length) {
        setFormErrors(errors);
        setIsSubmitting(false);
      } else {
        if (addTrader) {
          await postTraderDetails(payload);
        } else {
          await putTraderDetails(payload, companyId || sessionStorage.getItem('companyid'));
        }
        setOpen(false);
        setIsSubmitting(false);
        setTimeout(apiCall, 1000);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const disableTradersData = () => {
    deleteTrader(traderData, companyId || sessionStorage.getItem('companyid'));
    try {
      setTimeout(apiCall, 1000);
    } catch (err) {
      console.log(err);
    }
    setOpen(false);
  };
  return (
    <>
      <div className="settings-description">
        <span>
          Each time you add a new Consignor, Consignee or Carrier to an ENS Declaration, it will be added to your Trader
          Data for future use.
        </span>
      </div>
      <Paper sx={{ width: '100%' }}>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {rowData?.length > 0 && (
              <DataGrid
                rows={rowData}
                columns={columns}
                getRowId={() => Math.floor(Math.random() * 100000000)}
                initialState={{ pagination: { paginationModel } }}
                pageSizeOptions={[20, 50, 100]}
                sx={{ border: 0 }}
                onRowClick={handleClick}
                onCellDoubleClick={(params, event) => event.stopPropagation()}
              />
            )}
            <Dialog
              open={open}
              onClose={handleClose}
              className="settings-dialog"
              PaperProps={{
                component: 'form',
                onSubmit: (event) => {
                  event.preventDefault();
                  const formData = new FormData(event.currentTarget);
                  const formJson = Object.fromEntries(formData.entries());
                  const email = formJson.email;
                  handleClose();
                },
              }}
            >
              <DialogTitle>Trader Details</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <Box className="field">
                    {/* Label */}
                    <Typography variant="body1" className="field-label">
                      Country*
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      select
                      className="field-component"
                      value={traderData.Country || ''}
                      onChange={handleOnChange}
                      name="Country"
                      inputProps={{
                        maxLength: 2,
                      }}
                      error={!!formErrors.CountryCode}
                      helperText={formErrors.countryCode}
                    >
                      {countriesData.map((country) => (
                        <MenuItem key={country.countryCode} value={country.countryCode}>
                          {country.countryName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box className="field">
                    {/* Label */}
                    <Typography variant="body1" className="field-label">
                      {countryRequiresEORI(traderData.Country) ? 'EORI*' : 'EORI'}
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      className="field-component"
                      value={traderData.eori || ''}
                      onChange={handleOnChange}
                      onBlur={handleOnBlur}
                      name="eori"
                      error={!!formErrors.eori}
                      helperText={formErrors.eori}
                      placeholder={countryRequiresEORI(traderData.Country) ? '' : 'Optional for non-EU countries'}
                    />
                  </Box>

                  <Box className="field">
                    {/* Label */}
                    <Typography variant="body1" className="field-label">
                      Company Name*
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      className="field-component"
                      value={traderData.companyName || ''}
                      onChange={handleOnChange}
                      onBlur={handleOnBlur}
                      name="companyName"
                      error={!!formErrors.CompanyName}
                      helperText={formErrors.CompanyName}
                    />
                  </Box>
                  <Box className="field">
                    {/* Label */}
                    <Typography variant="body1" className="field-label">
                      Street Number*
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      className="field-component"
                      value={traderData.streetNumber || ''}
                      onChange={handleOnChange}
                      onBlur={handleOnBlur}
                      name="streetNumber"
                      error={!!formErrors.streetNumber}
                      helperText={formErrors.streetNumber}
                    />
                  </Box>
                  <Box className="field">
                    {/* Label */}
                    <Typography variant="body1" className="field-label">
                      Street Address*
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      className="field-component"
                      value={traderData.streetAddress || ''}
                      onChange={handleOnChange}
                      onBlur={handleOnBlur}
                      name="streetAddress"
                      error={!!formErrors.StreetAddress}
                      helperText={formErrors.StreetAddress}
                    />
                  </Box>
                  <Box className="field">
                    {/* Label */}
                    <Typography variant="body1" className="field-label">
                      Town / City*
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      className="field-component"
                      value={traderData.townCity || ''}
                      onChange={handleOnChange}
                      onBlur={handleOnBlur}
                      name="townCity"
                      error={!!formErrors.City}
                      helperText={formErrors.City}
                    />
                  </Box>
                  <Box className="field">
                    {/* Label */}
                    <Typography variant="body1" className="field-label">
                      Postcode*
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      className="field-component"
                      value={traderData.postCode || ''}
                      onChange={handleOnChange}
                      onBlur={handleOnBlur}
                      name="postCode"
                      error={!!formErrors.PostalCode}
                      helperText={formErrors.PostalCode}
                    />
                  </Box>
                  <FormControl error={!!formErrors?.checkboxes} component="fieldset">
                    <CustomCheckbox
                      onChange={handleOnChange}
                      checked={traderData.isConsignor || false}
                      name="isConsignor"
                      label="Consignor (or buyer)"
                      className="field-component"
                    />
                    <CustomCheckbox
                      onChange={handleOnChange}
                      checked={traderData.isConsignee || false}
                      name="isConsignee"
                      label="Consignee (or seller)"
                      className="field-component"
                    />
                    <CustomCheckbox
                      onChange={handleOnChange}
                      checked={traderData.isCarrier || false}
                      name="isCarrier"
                      label="Carrier"
                      className="field-component"
                    />
                    <FormHelperText>{formErrors?.checkboxes}</FormHelperText>
                  </FormControl>
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ padding: '24px', justifyContent: 'space-between' }}>
                {!addTrader && (
                  <Button className="button-class deny-button" onClick={disableTradersData}>
                    Delete this Trader Data
                  </Button>
                )}
                <Button onClick={handleEdit} className="button-class" disabled={isLoading}>
                  Save and Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Paper>
    </>
  );
}

DataTable.propTypes = {
  companyId: PropTypes.string,
};
